import React from "react";
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import VehicleSelector from "./VehicleSelector";

function VehicleSummary({ vehicle }) {
  if (!vehicle) {
    return (
      <Typography variant="body2">Noch kein Fahrzeugkontext ausgewählt.</Typography>
    );
  }

  if (vehicle.type === "catalog-selection") {
    return (
      <Stack spacing={0.5}>
        <Typography variant="subtitle2">Fahrzeugauswahl</Typography>
        <Typography variant="body2">
          {vehicle.make} · {vehicle.model}
        </Typography>
        <Typography variant="body2">Variante: {vehicle.modelVariant}</Typography>
        <Typography variant="body2">Motor: {vehicle.engine}</Typography>
        <Typography variant="body2">
          Vehicle Key: {vehicle.vehicleKey || "—"} · K-Type: {vehicle.ktype || "—"}
        </Typography>
      </Stack>
    );
  }

  if (vehicle.type === "vin") {
    return (
      <Stack spacing={0.5}>
        <Typography variant="subtitle2">VIN-Suche</Typography>
        <Typography variant="body2">VIN: {vehicle.vin}</Typography>
      </Stack>
    );
  }

  if (vehicle.type === "hsn-tsn") {
    return (
      <Stack spacing={0.5}>
        <Typography variant="subtitle2">HSN/TSN-Suche</Typography>
        <Typography variant="body2">
          HSN: {vehicle.hsn} · TSN: {vehicle.tsn}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={0.5}>
      <Typography variant="subtitle2">Aktiver Kontext</Typography>
      <Typography variant="body2">{JSON.stringify(vehicle)}</Typography>
    </Stack>
  );
}

export default function VehicleSearchPanel({
  vehicle,
  onVehicleSelection,
  onVinSubmit,
  onHsnTsnSubmit,
  onReset,
}) {
  const [tab, setTab] = React.useState(0);
  const [vin, setVin] = React.useState("");
  const [hsn, setHsn] = React.useState("");
  const [tsn, setTsn] = React.useState("");
  const [selectorKey, setSelectorKey] = React.useState(0);

  const normalizedVin = vin.trim().toUpperCase();
  const normalizedHsn = hsn.trim().toUpperCase();
  const normalizedTsn = tsn.trim().toUpperCase();

  const isVinValid = normalizedVin.length >= 8;
  const isHsnValid = normalizedHsn.length === 4;
  const isTsnValid = normalizedTsn.length >= 3;

  const handleVinSubmit = (event) => {
    event.preventDefault();
    if (!isVinValid) {
      return;
    }
    onVinSubmit?.(normalizedVin);
  };

  const handleHsnTsnSubmit = (event) => {
    event.preventDefault();
    if (!isHsnValid || !isTsnValid) {
      return;
    }
    onHsnTsnSubmit?.({ hsn: normalizedHsn, tsn: normalizedTsn });
  };

  const handleGlobalReset = () => {
    onReset?.();
    setSelectorKey((prev) => prev + 1);
    setVin("");
    setHsn("");
    setTsn("");
  };

  return (
    <Paper sx={{ p: 3 }} elevation={1}>
      <Typography variant="h6" gutterBottom>
        Fahrzeugsuche
      </Typography>
      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
        <Tab label="Fahrzeugauswahl" />
        <Tab label="VIN" />
        <Tab label="HSN/TSN" />
      </Tabs>
      {tab === 0 && (
        <VehicleSelector
          key={selectorKey}
          onVehicleSelected={onVehicleSelection}
          onSelectionReset={onReset}
        />
      )}
      {tab === 1 && (
        <Box component="form" onSubmit={handleVinSubmit} sx={{ display: "flex", gap: 2 }}>
          <TextField
            label="VIN"
            value={vin}
            onChange={(event) => setVin(event.target.value.toUpperCase())}
            placeholder="WBA..."
            fullWidth
            inputProps={{ maxLength: 17 }}
            helperText="Mindestens 8 Zeichen"
            error={Boolean(vin) && !isVinValid}
          />
          <Button type="submit" variant="contained" disabled={!isVinValid}>
            Suchen
          </Button>
        </Box>
      )}
      {tab === 2 && (
        <Box component="form" onSubmit={handleHsnTsnSubmit}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="HSN"
              value={hsn}
              onChange={(event) => setHsn(event.target.value.toUpperCase())}
              placeholder="0603"
              inputProps={{ maxLength: 4 }}
              helperText="4 Zeichen"
              error={Boolean(hsn) && !isHsnValid}
            />
            <TextField
              label="TSN"
              value={tsn}
              onChange={(event) => setTsn(event.target.value.toUpperCase())}
              placeholder="BHY"
              inputProps={{ maxLength: 8 }}
              helperText="Mindestens 3 Zeichen"
              error={Boolean(tsn) && !isTsnValid}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={!isHsnValid || !isTsnValid}
              sx={{ alignSelf: "center" }}
            >
              Suchen
            </Button>
          </Stack>
        </Box>
      )}
      <Box sx={{ mt: 3 }}>
        <Alert
          severity={vehicle ? "info" : "warning"}
          variant={vehicle ? "standard" : "outlined"}
          action={
            onReset ? (
              <Button color="inherit" size="small" onClick={handleGlobalReset}>
                Kontext löschen
              </Button>
            ) : null
          }
        >
          <VehicleSummary vehicle={vehicle} />
        </Alert>
      </Box>
    </Paper>
  );
}
